/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { Icon } from '@adobe-commerce/elsie/components/Icon';
import '@adobe-commerce/elsie/components/Input/Input.css';
import { CheckWithCircle, WarningWithCircle } from '@adobe-commerce/elsie/icons';
import { VComponent, classes, debounce } from '@adobe-commerce/elsie/lib';
import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { useCallback } from 'preact/hooks';

export interface InputProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'size' | 'icon'> {
  id?: string;
  name?: string;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  error?: boolean;
  floatingLabel?: string;
  onUpdateError?: (error: Error) => void;
  onValue?: (value: any) => void;
  size?: 'medium' | 'large';
  success?: boolean;
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  maxLength?: number;
}

export const Input: FunctionComponent<InputProps> = ({
  name,
  value,
  variant = 'primary',
  className,
  disabled,
  error,
  floatingLabel,
  onValue,
  onUpdateError,
  size = 'medium',
  icon,
  maxLength,
  success,
  ...props
}) => {
  const id = props?.id || name || `dropin-input-${Math.random().toString(36)}`;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnValueHandler = useCallback(
    debounce(async (newValue: any) => {
      if (onValue) {
        try {
          await onValue(newValue);
        } catch (e) {
          if (onUpdateError) {
            onUpdateError(e as Error);
          }
        }
      }
    }, 200),
    [onValue, onUpdateError]
  );

  const handleOnValue = (e: Event) => {
    const input = e.target as HTMLInputElement;

    debouncedOnValueHandler(input.value.trim());
  };

  return (
    <div
      className={classes([
        'dropin-input-container',
        `dropin-input-container--${variant}`,
        [`dropin-input-container--floating`, !!floatingLabel],
        ['dropin-input-container--disabled', disabled],
      ])}
    >
      {icon && (
        <VComponent
          node={icon}
          className={classes([
            'dropin-input__field-icon--left',
            icon.props.className,
          ])}
        />
      )}
      <div className="dropin-input-label-container">
        {/* Input */}
        <input
          id={id}
          onChange={handleOnValue}
          type="text"
          maxLength={maxLength}
          name={name}
          value={value}
          {...props}
          className={classes([
            'dropin-input',
            `dropin-input--${size}`,
            `dropin-input--${variant}`,
            [`dropin-input--error`, !!error],
            [`dropin-input--success`, !!success],
            ['dropin-input--disabled', disabled],
            [`dropin-input--floating`, !!floatingLabel],
            [`dropin-input--icon-left`, !!icon],
            className,
          ])}
          disabled={disabled}
        />

        {/* Floating Label */}
        {floatingLabel && (
          <label
            htmlFor={id}
            className={classes([
              [`dropin-input__label--floating`, !!floatingLabel],
              [`dropin-input__label--floating--icon-left`, !!icon],
              [`dropin-input__label--floating--error`, !!error],
            ])}
          >
            {floatingLabel}
          </label>
        )}
      </div>

      {error && (
        <div
          className={classes([
            'dropin-input__field-icon--right',
            'dropin-input__field-icon--error',
          ])}
        >
          <Icon
            source={WarningWithCircle}
            size="16"
            stroke="2"
            className="dropin-input--warning-icon"
            viewBox="-1 -1 26 26"
          />
        </div>
      )}

      {success && (
        <div
          className={classes([
            'dropin-input__field-icon--right',
            'dropin-input__field-icon--success',
          ])}
        >
          <Icon
            source={CheckWithCircle}
            size="16"
            stroke="2"
            className="dropin-input--success-icon"
            viewBox="-1 -1 26 26"
          />
        </div>
      )}
    </div>
  );
};
